import * as React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import createApolloClient from "../lib/apolloClient";
import { ApolloProvider } from "@apollo/client/react";

export const CustomApolloProvider: React.FC<{}> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  /*
   * The token getter lives behind a ref so a new one never forces a new client.
   *
   * This component re-renders whenever Auth0's state object changes, and Auth0
   * bumps `user.updated_at` on silent token renewal — which the auth link
   * triggers on every single GraphQL request. Building the client inline meant
   * each of those renders produced a fresh ApolloClient with a fresh, empty
   * InMemoryCache. Every active query snapped back to `loading`, the job page
   * rendered `null` because it had no job, and the open inspection dialog was
   * unmounted mid-report with everything the technician had typed in it.
   *
   * The client is now built once per authenticated session and reads the current
   * getter through the ref when it needs a token.
   */
  const getTokenRef = React.useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  const client = React.useMemo(
    () => (isAuthenticated ? createApolloClient(() => getTokenRef.current()) : null),
    [isAuthenticated]
  );

  if (!client) return null;

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
